import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, AlertTriangle } from "lucide-react";
import { Reveal } from "../marketing/Reveal";
import { StepIndicator } from "./StepIndicator";
import { StepAboutYou } from "./StepAboutYou";
import { ChoiceGrid } from "./ChoiceGrid";
import { StepDetails, StepReview } from "./StepDetails";
import { QuoteSuccess } from "./QuoteSuccess";
import { EMPTY_FORM, WEBSITE_TYPES, FEATURES, BUDGETS, STEP_LABELS, validateStep } from "./quoteConfig";
import { submitQuote, apiError } from "@/lib/api";

const stepTitles = [
  ["About you", "Who should we contact about your quote?"],
  ["Your website", "What type of website do you need?"],
  ["Features", "Which features would you like? Select all that apply."],
  ["Budget", "What is your approximate budget?"],
  ["Project details", "Help us understand your business and goals."],
  ["Review & submit", "Almost done — check everything looks right."],
];

export const QuoteForm = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");
  const submitting = useRef(false);
  const topRef = useRef(null);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };
  const select = (name) => (opt) => {
    setForm((f) => ({ ...f, [name]: opt }));
    setErrors((er) => ({ ...er, [name]: undefined }));
  };
  const toggleFeature = (opt) => setForm((f) => ({ ...f, features: f.features.includes(opt) ? f.features.filter((x) => x !== opt) : [...f.features, opt] }));

  const go = (n) => {
    setDir(n > step ? 1 : -1);
    setStep(n);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };
  const next = () => {
    const e = validateStep(step, form);
    setErrors(e);
    if (Object.keys(e).length) return;
    go(step + 1);
  };

  const submit = async () => {
    if (submitting.current || status === "loading" || status === "success") return;
    submitting.current = true;
    setStatus("loading");
    setServerError("");
    try {
      const data = await submitQuote({ ...form, full_name: form.full_name.trim(), business_name: form.business_name.trim(), email: form.email.trim(), details: form.details.trim() });
      if (data.redirect) {
        navigate(data.redirect);
        return;
      }
      setStatus("success");
    } catch (err) {
      setServerError(apiError(err, "We couldn't submit your request right now. Please try again or call us directly."));
      setStatus("error");
    } finally {
      submitting.current = false;
    }
  };

  const [title, sub] = stepTitles[step];

  return (
    <section id="quote" className="section overflow-hidden" aria-labelledby="quote-title">
      <div className="orb left-0 top-20 h-[460px] w-[460px] bg-indigo-600/15" aria-hidden="true" />
      <div className="container-x relative">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal><p className="eyebrow">Free quote</p></Reveal>
          <Reveal delay={0.05}><h2 id="quote-title" className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">Let's Build Your Website</h2></Reveal>
          <Reveal delay={0.1}><p className="mt-4 text-base md:text-lg text-slate-400">Tell us about your project and we'll get back to you with a quote.</p></Reveal>
        </div>

        <Reveal delay={0.15} className="mx-auto mt-12 max-w-4xl">
          <div ref={topRef} className="relative rounded-[1.75rem] p-[1px] bg-gradient-to-b from-white/15 via-white/5 to-transparent scroll-mt-28">
            <div className="glass rounded-[1.75rem] p-6 sm:p-10" data-testid="quote-form-card">
              {status === "success" ? (
                <QuoteSuccess />
              ) : (
                <form noValidate onSubmit={(e) => { e.preventDefault(); step === STEP_LABELS.length - 1 ? submit() : next(); }} data-testid="quote-form">
                  <StepIndicator current={step} />
                  <div className="mt-9 mb-7">
                    <p className="font-mono text-xs text-indigo-300/80">Step {step + 1} of {STEP_LABELS.length}</p>
                    <h3 className="mt-2 text-xl sm:text-2xl font-semibold text-white">{title}</h3>
                    <p className="mt-1.5 text-sm sm:text-base text-slate-400">{sub}</p>
                  </div>

                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, x: dir * 28 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: dir * -28 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {step === 0 && <StepAboutYou form={form} errors={errors} onChange={onChange} />}
                      {step === 1 && <div data-testid="quote-step-2"><ChoiceGrid name="website_type" legend="Website type" options={WEBSITE_TYPES} value={form.website_type} onSelect={select("website_type")} error={errors.website_type} testPrefix="quote-type" /></div>}
                      {step === 2 && <div data-testid="quote-step-3"><ChoiceGrid name="features" legend="Features" multiple options={FEATURES} value={form.features} onSelect={toggleFeature} testPrefix="quote-feature" hint="Optional — you can skip this step if you're not sure yet." /></div>}
                      {step === 3 && <div data-testid="quote-step-4"><ChoiceGrid name="budget" legend="Budget range" options={BUDGETS} value={form.budget} onSelect={select("budget")} error={errors.budget} testPrefix="quote-budget" hint="Budget ranges are for quoting purposes only — they are not fixed service prices." /></div>}
                      {step === 4 && <StepDetails form={form} errors={errors} onChange={onChange} />}
                      {step === 5 && <StepReview form={form} />}
                    </motion.div>
                  </AnimatePresence>

                  {status === "error" && (
                    <div role="alert" className="mt-6 flex items-start gap-3 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200" data-testid="quote-submit-error">
                      <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" /> {serverError}
                    </div>
                  )}

                  <div className="mt-9 flex flex-col-reverse sm:flex-row items-center justify-between gap-3 border-t border-white/[0.08] pt-7">
                    <button type="button" onClick={() => go(step - 1)} disabled={step === 0 || status === "loading"} className="btn-ghost w-full sm:w-auto" data-testid="quote-prev-btn">
                      <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
                    </button>
                    {step < STEP_LABELS.length - 1 ? (
                      <button type="submit" className="btn-primary w-full sm:w-auto group" data-testid="quote-next-btn">
                        Continue <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                      </button>
                    ) : (
                      <button type="submit" disabled={status === "loading"} className="btn-primary w-full sm:w-auto !px-8" data-testid="quote-submit-btn" aria-busy={status === "loading"}>
                        {status === "loading" ? (<><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Submitting…</>) : "Request My Free Quote"}
                      </button>
                    )}
                  </div>
                </form>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
